import axios from "axios";
import { useAuthStore } from "../stores/authStore.js";
import { useActiveBranchStore } from "../stores/activeBranchStore.js";

import { getApiOrigin } from "./apiOrigin.js";

const API_BASE_URL = getApiOrigin();

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 90000, // Google Sheets อาจช้า (กัน timeout ตอนบันทึก/ล็อกอิน)
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});

// A raw client without interceptors for token refresh (avoid infinite loops)
const rawClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 90000,
  headers: {
    "Content-Type": "application/json",
  },
});

let _refreshInFlight = null;

// Request interceptor - เพิ่ม cache busting + auth header
apiClient.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent browser/proxy caching (as query param)
    config.params = config.params || {};
    config.params._t = Date.now();

    // Append branchCode automatically if active branch selected and not ALL.
    // IMPORTANT: prefer config.params over mutating config.url, to avoid dropping caller params.
    try {
      const activeBranchStore = useActiveBranchStore();
      if (activeBranchStore?.active && activeBranchStore.active !== "ALL") {
        const urlHasBranchCode = /([&?])branchCode=/.test(String(config.url || ""));
        const paramsHasBranchCode = Object.prototype.hasOwnProperty.call(config.params, "branchCode");
        if (!urlHasBranchCode && !paramsHasBranchCode) {
          config.params.branchCode = activeBranchStore.active;
        }
      }
    } catch (e) {
      // ignore if store not ready yet
    }

    // Attach Authorization header if token exists (lazy access to store to avoid circular import during build)
    try {
      const authStore = useAuthStore();
      if (authStore.token) {
        config.headers.Authorization = `Bearer ${authStore.token}`;
      }
    } catch (e) {
      // Store might not be initialized yet (e.g., early import) - ignore
    }

    const paramsLog = config.params ? ` params=${JSON.stringify(config.params)}` : "";
    console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}${paramsLog}`);
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response: ${response.status}`, response.data);

    // ตรวจสอบว่าข้อมูลเป็น array ว่างหรือไม่
    if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length === 0) {
      console.log("📭 API returned empty array - ไม่มีข้อมูลใน Google Sheets");
    }

    return response;
  },
  async (error) => {
    const data = error.response?.data || error;
    const status = error.response?.status;
    const originalRequest = error.config || {};

    // If the server returned JSON/text but axios is in `responseType: "blob"`, parse the Blob
    // so downstream code can read `message` instead of getting a raw Blob object.
    try {
      if (typeof Blob !== "undefined" && data instanceof Blob) {
        const contentType = String(data.type || "").toLowerCase();
        const text = await data.text();

        if (contentType.includes("application/json") || text.trim().startsWith("{")) {
          const parsed = JSON.parse(text || "{}");
          parsed._status = status;
          console.error("❌ API Error (parsed blob):", parsed);
          return Promise.reject(parsed);
        }

        const parsedText = {
          success: false,
          message: text || "Request failed",
          _status: status,
        };
        console.error("❌ API Error (blob text):", parsedText);
        return Promise.reject(parsedText);
      }
    } catch (parseErr) {
      // Fall back to original behavior
      console.warn("[api] blob error parse failed", parseErr?.message || parseErr);
    }

    // Try refresh flow on 401 once (skip login/refresh endpoints)
    if (
      status === 401 &&
      !originalRequest._retry &&
      !String(originalRequest.url || "").includes("/auth/login") &&
      !String(originalRequest.url || "").includes("/auth/refresh")
    ) {
      try {
        const authStore = useAuthStore();
        if (authStore?.refreshToken) {
          originalRequest._retry = true;

          if (!_refreshInFlight) {
            _refreshInFlight = rawClient
              .post("/auth/refresh", { refreshToken: authStore.refreshToken })
              .then((res) => {
                const token = res.data?.token;
                const refreshToken = res.data?.refreshToken;
                const user = res.data?.user;
                if (token) {
                  authStore.setSession({ token, refreshToken, user });
                }
                return token;
              })
              .finally(() => {
                _refreshInFlight = null;
              });
          }

          const newToken = await _refreshInFlight;
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        }
      } catch (e) {
        // fall through to logout
        console.warn("[api] refresh attempt failed", e?.message || e);
      }
    }

    // Auto logout on 401 if refresh not possible/failed
    if (status === 401) {
      try {
        const authStore = useAuthStore();
        if (authStore.isAuthenticated) {
          authStore.logout();
        }
      } catch (e) {
        console.warn("[api] auto logout failed", e?.message || e);
      }
    }

    console.error("❌ API Error:", data);
    return Promise.reject(data);
  },
);

export default apiClient;
